import React, { useMemo } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { servesLabel, suggestFor } from "../../utils/serves";

const INK = "#1D1A18";
const MUTED = "#5F5955";
const LINE = "#ECE6E0";
const MAX_SHOWN = 6;

/**
 * "Quick picks" on a restaurant's Catering view (backend migration 135),
 * after ezCater's: the catering items that say how many they serve, each card
 * showing its price and "Serves N". Nearest to the event's guest count first
 * (from the Catering page), else menu order. Tapping one opens the item.
 * Shows nothing until some catering item has a serves number.
 */
export default function CateringSuggestions({ foods, guests, onOpen, currencySymbol = "$" }) {
  const picks = useMemo(() => {
    const list = (foods || []).filter((f) => !f.is86ed && servesLabel(f));
    if (!(guests > 0)) return list.slice(0, MAX_SHOWN);
    const fit = (f) => {
      const s = suggestFor(f, guests);
      return s ? s.feeds - guests : Infinity;
    };
    return list
      .map((food, i) => ({ food, i, fit: fit(food) }))
      .sort((a, b) => a.fit - b.fit || a.i - b.i)
      .map((p) => p.food)
      .slice(0, MAX_SHOWN);
  }, [foods, guests]);

  if (!picks.length) return null;

  return (
    <Box component="section" aria-labelledby="catering-quick-picks" sx={{ mb: 3.5 }}>
      <Typography
        id="catering-quick-picks"
        component="h2"
        sx={{ mb: 1.75, fontSize: { xs: "1.15rem", md: "1.3rem" }, fontWeight: 800, letterSpacing: "-0.02em", color: INK, textTransform: "uppercase" }}
      >
        Quick picks
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
        {picks.map((food) => {
          const prices = (food.variations || []).map((v) => parseFloat(v.price) || 0);
          const low = prices.length ? Math.min(...prices) : 0;
          const price = `${prices.length > 1 ? "From " : ""}${currencySymbol}${low.toFixed(2)}`;
          const serves = servesLabel(food);
          return (
            <ButtonBase
              key={food._id}
              onClick={() => onOpen(food)}
              aria-label={`${food.title}, ${price}, ${serves}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr) 120px", sm: "minmax(0, 1fr) 40%" },
                alignItems: "stretch",
                width: "100%",
                minHeight: { xs: 128, sm: 148 },
                textAlign: "left",
                borderRadius: "16px",
                overflow: "hidden",
                border: `1px solid ${LINE}`,
                bgcolor: "#fff",
                transition: "box-shadow .2s ease, border-color .2s ease",
                "&:hover": { borderColor: "#DDD5CE", boxShadow: "0 8px 24px -14px rgba(29,26,24,0.35)" },
                "&:focus-visible": { outline: `3px solid ${INK}`, outlineOffset: 2 },
              }}
            >
              <Box sx={{ minWidth: 0, p: { xs: 1.75, sm: 2 }, display: "flex", flexDirection: "column" }}>
                <Typography noWrap sx={{ fontWeight: 800, fontSize: { xs: "1rem", sm: "1.08rem" }, color: INK, letterSpacing: "-0.01em" }}>
                  {food.title}
                </Typography>
                {food.description && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: "0.88rem",
                      lineHeight: 1.45,
                      color: MUTED,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {food.description}
                  </Typography>
                )}
                <Typography sx={{ mt: "auto", pt: 1.25, fontSize: "0.95rem", color: MUTED, fontVariantNumeric: "tabular-nums" }}>
                  <Box component="span" sx={{ fontWeight: 800, color: INK }}>
                    {price}
                  </Box>
                  <Box component="span" sx={{ mx: 1, color: "#DDD5CE" }} aria-hidden>
                    |
                  </Box>
                  {serves}
                </Typography>
              </Box>
              <Box
                aria-hidden
                sx={{
                  position: "relative",
                  bgcolor: "#F7F3EF",
                  backgroundImage: food.image ? `url("${String(food.image).replace(/"/g, "%22")}")` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    right: 10,
                    bottom: 10,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    color: INK,
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "0 2px 8px rgba(29,26,24,0.25)",
                  }}
                >
                  <AddRoundedIcon sx={{ fontSize: 22 }} />
                </Box>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
